import { useEffect, useState } from "react";
import type { Screen } from "src/domain/SearchScreen/SearchScreen";
import type { GeocodeResult } from "src/api/GeocodeResponse";
import { sendAnalyticsNonInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { safeSessionStorage } from "../safeLocalStorage";
import { startInactivityTimeout } from "../startInactivityTimeout";
import { useScreenMinWidth } from "./useScreenMinWidth";

const CLICKTRIPZ_SRC = "https://static.clicktripz.com/tag.js";
const DESKTOP_INACTIVITY_TIMEOUT = 30000; // 30 seconds
const MOBILE_INACTIVITY_TIMEOUT = 10000; // 10 seconds

const ClickTripzEnabled: Screen[] = [
  "Search Results",
  "Route",
  "Hotels",
  "Segment",
];

type Props = {
  screen: Screen;
  requestId?: string;
  geocodeResult?: GeocodeResult;
};

export function useClickTripz({ screen, requestId, geocodeResult }: Props) {
  const isTest = import.meta.env.MODE === "test";
  const isScreenEnabled = ClickTripzEnabled.includes(screen);
  const { isMobile } = useScreenMinWidth();
  const [hasUserBeenInactive, setHasUserBeenInactive] = useState(
    safeSessionStorage.getItem("inactiveUser") === "true",
  );

  useEffect(() => {
    if (hasUserBeenInactive) return;

    return startInactivityTimeout(
      (timeInactive: number) => {
        sendAnalyticsNonInteractionEvent({
          category: "Screen",
          action: "InactivityTimeout",
          label: `TimeInactive:${timeInactive}ms`,
        });
        safeSessionStorage.setItem("inactiveUser", "true");
        setHasUserBeenInactive(true);
        sendAnalyticsNonInteractionEvent({
          category: "Screen",
          action: "ClickTripzRequestSent",
        });
      },
      isMobile ? MOBILE_INACTIVITY_TIMEOUT : DESKTOP_INACTIVITY_TIMEOUT,
    );
  }, [isMobile, hasUserBeenInactive]);

  useEffect(() => {
    if (isTest) return;

    if (!hasUserBeenInactive) {
      const script = document.createElement("script");
      script.async = true;
      script.setAttribute("data-publisher", "2ba96a30a24f499d9262de89964c9307");
      script.src = CLICKTRIPZ_SRC;
      document.body.appendChild(script);
    }
  }, [isTest, hasUserBeenInactive]);

  const isEnabled = isScreenEnabled && hasUserBeenInactive;

  useEffect(() => {
    if (isTest) return;

    window._CTZ = {
      ...window._CTZ,
      enabled: isEnabled,
      verticals: {
        ...window._CTZ?.verticals,
        hotel: {
          active: isEnabled,
          search: {
            countryCode: geocodeResult?.countryCode,
            country: geocodeResult?.countryName,
            city: geocodeResult?.canonicalName,
          },
        },
      },
      extra: {
        requestId: requestId,
      },
    };

    window._CTZ_RELOAD?.();
  }, [
    isEnabled,
    requestId,
    geocodeResult?.canonicalName,
    geocodeResult?.countryCode,
    geocodeResult?.countryName,
    isTest,
  ]);
}
